import type { HikingMetadata } from "~/lib/api";
import { cn } from "~/lib/utils";
import { Button } from "~/components/ui/button";
import { Icon } from "~/components/ui/icons/icon";
import { Input } from "~/components/ui/input";
import { UnitToggle } from "~/components/ui/unit-toggle";
import { useInlineEditor } from "~/hooks/useInlineEditor";

type ElevationUnit = "m" | "ft";
type Elevation = NonNullable<HikingMetadata["elevation"]>;

interface ElevationEditorProps {
  value: Elevation | null | undefined;
  onChange: (value: Elevation | null) => void;
  canEdit?: boolean;
  disabled?: boolean;
}

export function ElevationEditor({ value, onChange, canEdit, disabled }: ElevationEditorProps) {
  const {
    editing,
    saving,
    editState,
    containerRef,
    primaryInputRef,
    startEditing,
    save,
    handleKeyDown,
    handleBlur,
    setField,
  } = useInlineEditor<Elevation>({
    value,
    onSave: onChange,
    toEditState: (v) => ({
      gain: v?.gain?.toString() ?? "",
      loss: v?.loss?.toString() ?? "",
      unit: v?.unit ?? "m",
    }),
    fromEditState: (state) => {
      const gain = parseFloat(state.gain);
      const loss = parseFloat(state.loss);
      // Require positive values - zero elevation is meaningless
      const hasGain = state.gain.trim() && !isNaN(gain) && gain > 0;
      const hasLoss = state.loss.trim() && !isNaN(loss) && loss > 0;

      // Both empty/invalid/zero = clear
      if (!hasGain && !hasLoss) {
        return null;
      }

      return {
        gain: hasGain ? gain : 0,
        loss: hasLoss ? loss : 0,
        unit: state.unit as ElevationUnit,
      };
    },
  });

  const hasDisplayableValue = value && (value.gain > 0 || value.loss > 0);

  if (editing) {
    return (
      <div ref={containerRef} className="flex flex-wrap gap-2 items-center" onBlur={handleBlur}>
        <div className="flex items-center gap-1">
          <span className="text-xs text-muted-foreground">↑</span>
          <Input
            ref={primaryInputRef}
            type="number"
            min="0"
            step="1"
            value={editState.gain}
            onChange={(e) => setField("gain", e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="0"
            disabled={saving}
            className="w-24 h-8 text-sm"
            aria-label="Elevation gain"
          />
        </div>
        <div className="flex items-center gap-1">
          <span className="text-xs text-muted-foreground">↓</span>
          <Input
            type="number"
            min="0"
            step="1"
            value={editState.loss}
            onChange={(e) => setField("loss", e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="0"
            disabled={saving}
            className="w-24 h-8 text-sm"
            aria-label="Elevation loss"
          />
        </div>
        <UnitToggle
          value={editState.unit as ElevationUnit}
          onChange={(unit) => setField("unit", unit)}
          options={["m", "ft"]}
          label="Elevation unit"
          disabled={saving}
        />
        <Button size="icon-sm" variant="ghost" onMouseDown={(e) => e.preventDefault()} onClick={save} disabled={saving}>
          <Icon i="check" className="size-6 text-primary" />
        </Button>
      </div>
    );
  }

  if (hasDisplayableValue) {
    const display = <ElevationDisplay elevation={value} />;

    if (canEdit) {
      return (
        <button
          type="button"
          onClick={startEditing}
          disabled={disabled}
          className="text-foreground hover:text-primary transition-colors disabled:opacity-50"
        >
          {display}
        </button>
      );
    }

    return display;
  }

  if (canEdit) {
    return (
      <button
        onClick={startEditing}
        disabled={disabled}
        className="text-sm text-muted-foreground hover:text-primary transition-colors cursor-pointer"
      >
        + Add elevation
      </button>
    );
  }

  return null;
}

interface ElevationDisplayProps {
  elevation: Elevation;
  /** Show only gain (compact mode) */
  compact?: boolean;
  className?: string;
}

export function ElevationDisplay({ elevation, compact, className }: ElevationDisplayProps) {
  const { gain, loss, unit } = elevation;

  // Compact: show only gain (if any) or loss
  if (compact) {
    if (gain > 0) {
      return (
        <span className={cn("text-sm", className)}>
          ↑ {gain}
          {unit}
        </span>
      );
    }
    if (loss > 0) {
      return (
        <span className={cn("text-sm", className)}>
          ↓ {loss}
          {unit}
        </span>
      );
    }
    return null;
  }

  // Full display logic:
  // - Both zero: show nothing (shouldn't happen if hasElevation check is correct)
  // - Only gain: "↑ 450m"
  // - Only loss (descent-only): "↓ 450m"
  // - Both same (loop): "↑↓ 450m" (net zero)
  // - Both different: "↑ 450m ↓ 300m"

  const hasGain = gain > 0;
  const hasLoss = loss > 0;

  if (!hasGain && !hasLoss) {
    return null;
  }

  if (hasGain && hasLoss && gain === loss) {
    // Loop trail with equal gain/loss
    return (
      <span className={cn("text-sm", className)}>
        ↑↓ {gain}
        {unit}
      </span>
    );
  }

  if (hasGain && hasLoss) {
    // Different gain and loss
    return (
      <span className={cn("text-sm", className)}>
        ↑ {gain}
        {unit} ↓ {loss}
        {unit}
      </span>
    );
  }

  if (hasGain) {
    return (
      <span className={cn("text-sm", className)}>
        ↑ {gain}
        {unit}
      </span>
    );
  }

  // Descent-only trail
  return (
    <span className={cn("text-sm", className)}>
      ↓ {loss}
      {unit}
    </span>
  );
}
